import { supabase } from './supabase';
import { appBaseUrl } from './runtime';

const DEFAULT_DISCLAIMER = 'Conteúdo educativo, não substitui avaliação profissional.';
const DAY_MS = 24 * 60 * 60 * 1000;

function createTextId(prefix) {
    const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}_${String(random).replace(/-/g, '')}`;
}

function catalogId(prefix, value) {
    const candidate = String(value || '').trim();
    return candidate || createTextId(prefix);
}

function normalizeCatalogActivity(item = {}) {
    return {
        id: catalogId('activity', item.id),
        age_stage_id: String(item.age_stage_id || '').trim(),
        category_id: String(item.category_id || '').trim(),
        titulo: String(item.titulo || '').trim(),
        objetivo: String(item.objetivo || ''),
        materiais: Array.isArray(item.materiais) ? item.materiais : [],
        passos: Array.isArray(item.passos) ? item.passos : [],
        duracao_min: Math.max(1, Number(item.duracao_min) || 10),
        cuidados: String(item.cuidados || ''),
        imagem_url: item.imagem_url || null,
        disclaimer: item.disclaimer || DEFAULT_DISCLAIMER,
    };
}

function httpError(message, status = 400) {
    const error = new Error(message || 'Algo deu errado. Tente novamente.');
    error.response = { status, data: { detail: message } };
    return error;
}

function ensureData(data, error) {
    if (error) throw httpError(error.message || error.details || error.hint);
    return { data };
}

function todayDateOnly() {
    return new Date().toISOString().slice(0, 10);
}

function ageDaysFromDob(dob) {
    if (!dob) return 0;
    const birth = new Date(`${dob}T00:00:00`);
    if (Number.isNaN(birth.getTime())) return 0;
    return Math.max(0, Math.floor((new Date(`${todayDateOnly()}T00:00:00`) - birth) / DAY_MS));
}

function decorateActivity(activity) {
    if (!activity) return activity;
    const category = activity.categories || activity.category || null;
    const stage = activity.age_stages || activity.age_stage || null;
    return {
        ...activity,
        materiais: Array.isArray(activity.materiais) ? activity.materiais : [],
        passos: Array.isArray(activity.passos) ? activity.passos : [],
        disclaimer: activity.disclaimer || DEFAULT_DISCLAIMER,
        category_name: category?.nome,
        category_color: category?.cor,
        stage_title: stage?.titulo,
    };
}

function decorateChild(child, stages = []) {
    const age_days = ageDaysFromDob(child?.dob);
    const age_stage = stages.find((stage) => age_days >= stage.min_days && age_days <= stage.max_days);
    return {
        ...child,
        age_days,
        age_stage_id: age_stage?.id || null,
        age_stage,
    };
}

function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char] || char));
}

function decorateInvite(invite) {
    if (!invite) return invite;
    const expiresAt = new Date(invite.expires_at).getTime();
    return {
        ...invite,
        expired: !invite.used && Number.isFinite(expiresAt) && expiresAt <= Date.now(),
        link: invite.token ? `${appBaseUrl()}/convite/${encodeURIComponent(invite.token)}` : null,
    };
}

async function currentAuthUser() {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) throw httpError('Sua sessão expirou. Entre novamente.', 401);
    return data.user;
}

async function currentProfile() {
    const authUser = await currentAuthUser();
    const { data, error } = await supabase
        .from('users')
        .select('id, email, name, role, banned, accept_terms, active_child_id, created_at')
        .eq('id', authUser.id)
        .maybeSingle();
    if (error) throw httpError(error.message);
    if (data?.banned) {
        await supabase.auth.signOut();
        throw httpError('Esta conta está bloqueada. Fale com o suporte.', 403);
    }
    return data || {
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.name || 'Família',
        role: 'user',
        banned: false,
        accept_terms: Boolean(authUser.user_metadata?.accept_terms),
        active_child_id: null,
    };
}

async function stages() {
    const { data, error } = await supabase
        .from('age_stages')
        .select('id, slug, titulo, descricao, min_days, max_days, dados_gerais, desenvolvimento, dicas, cuidados')
        .order('min_days', { ascending: true });
    return ensureData(data || [], error).data;
}

async function childrenForCurrentUser() {
    const profile = await currentProfile();
    const { data, error } = await supabase
        .from('children')
        .select('id, user_id, nome, dob, foto_url, created_at, updated_at')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: true });
    const list = ensureData(data || [], error).data;
    let decorated = [];
    try {
        decorated = await stages();
    } catch {
        // A criança continua válida mesmo quando a consulta editorial de fases falha temporariamente.
    }
    return { profile, children: list.map((child) => decorateChild(child, decorated)) };
}

async function childrenContext() {
    const { profile, children } = await childrenForCurrentUser();
    return {
        children,
        activeChild: children.find((child) => child.id === profile.active_child_id) || children[0] || null,
    };
}

async function activeChild() {
    return (await childrenContext()).activeChild;
}

async function categories() {
    const { data, error } = await supabase
        .from('categories')
        .select('id, slug, nome, cor, icone')
        .order('nome', { ascending: true });
    return ensureData(data || [], error).data;
}

async function activities(params = {}) {
    let query = supabase
        .from('activities')
        .select('id, age_stage_id, category_id, titulo, objetivo, materiais, passos, duracao_min, cuidados, imagem_url, disclaimer, created_at, updated_at, categories(id, slug, nome, cor, icone), age_stages(id, slug, titulo)')
        .order('created_at', { ascending: true });
    if (params.age_stage_id) query = query.eq('age_stage_id', params.age_stage_id);
    if (params.category_id) query = query.eq('category_id', params.category_id);
    if (params.q) query = query.ilike('titulo', `%${String(params.q).replace(/%/g, '')}%`);
    const { data, error } = await query;
    return ensureData((data || []).map(decorateActivity), error).data;
}

async function activityById(id) {
    const { data, error } = await supabase
        .from('activities')
        .select('id, age_stage_id, category_id, titulo, objetivo, materiais, passos, duracao_min, cuidados, imagem_url, disclaimer, created_at, updated_at, categories(id, slug, nome, cor, icone), age_stages(id, slug, titulo)')
        .eq('id', id)
        .single();
    return ensureData(decorateActivity(data), error).data;
}

async function countRows(table) {
    const { count, error } = await supabase.from(table).select('id', { count: 'exact', head: true });
    if (error) throw httpError(error.message);
    return count || 0;
}

async function parseImport(body, config = {}) {
    const raw = typeof body === 'string' ? body : JSON.stringify(body);
    const contentType = config.headers?.['Content-Type'] || config.headers?.['content-type'] || '';
    if (contentType.includes('json') || raw.trim().startsWith('[') || raw.trim().startsWith('{')) {
        const parsed = typeof body === 'string' ? JSON.parse(body) : body;
        const items = Array.isArray(parsed) ? parsed : (parsed.activities || []);
        return items.map((item) => ({
            ...item,
            materiais: Array.isArray(item.materiais) ? item.materiais : [],
            passos: Array.isArray(item.passos) ? item.passos : [],
            disclaimer: item.disclaimer || DEFAULT_DISCLAIMER,
        }));
    }
    const [headerLine, ...rows] = raw.split(/\r?\n/).filter(Boolean);
    const headers = headerLine.split(',').map((item) => item.trim());
    return rows.map((line) => {
        const values = line.split(',').map((item) => item.trim());
        return headers.reduce((item, header, index) => ({ ...item, [header]: values[index] || '' }), {});
    });
}

async function adminUsers(params = {}) {
    let query = supabase
        .from('users')
        .select('id, email, name, role, banned, accept_terms, created_at')
        .order('created_at', { ascending: false });
    if (params.q) {
        const q = String(params.q).replace(/[(),]/g, '');
        query = query.or(`email.ilike.%${q}%,name.ilike.%${q}%`);
    }
    const { data, error } = await query;
    return ensureData(data || [], error).data;
}

async function assertStaff() {
    const profile = await currentProfile();
    if (!['super_admin', 'editor', 'moderador'].includes(profile.role)) {
        throw httpError('Você não tem permissão para esta ação.', 403);
    }
    return profile;
}

async function assertContentStaff() {
    const profile = await assertStaff();
    if (!['super_admin', 'editor'].includes(profile.role)) {
        throw httpError('Apenas administradores de conteúdo podem usar esta área.', 403);
    }
    return profile;
}

async function assertSuperAdmin() {
    const profile = await currentProfile();
    if (profile.role !== 'super_admin') throw httpError('Apenas super administradores podem gerenciar convites.', 403);
    return profile;
}

async function invokeAdminAI(payload) {
    const { data, error } = await supabase.functions.invoke('admin-ai', { body: payload });
    if (error) throw httpError(error.message || 'Não foi possível comunicar com o serviço de IA.');
    if (data?.error) throw httpError(data.error);
    return data?.data ?? data;
}

async function invokeEmailService(payload) {
    const { data, error } = await supabase.functions.invoke('email-service', { body: payload });
    if (error) throw httpError(error.message || 'Não foi possível comunicar com o serviço de e-mail.');
    if (data?.error) throw httpError(data.error?.message || data.error);
    return data?.data ?? data;
}

async function recordAudit(action, resource, resourceId = null, details = {}) {
    try {
        const { error } = await supabase.rpc('record_audit_event', { p_action: action, p_resource: resource, p_resource_id: resourceId, p_details: details });
        if (error) console.warn('[audit]', error.message);
    } catch (error) {
        console.warn('[audit]', error?.message || error);
    }
}

export async function recordAuditEvent(action, resource, resourceId = null, details = {}) {
    return recordAudit(action, resource, resourceId, details);
}

const api = {
    async get(path, config = {}) {
        if (path === '/auth/me') return { data: await currentProfile() };
        if (path === '/children') return { data: (await childrenForCurrentUser()).children };
        if (path === '/children/context') return { data: await childrenContext() };
        if (path === '/children/me') return { data: await activeChild() };
        if (path === '/pwa/install-config') {
            await currentAuthUser();
            const { data, error } = await supabase.from('app_settings').select('value_json').eq('key', 'pwa.install_prompt_interval_days').maybeSingle();
            if (error) throw httpError(error.message);
            const value = Number(data?.value_json);
            return { data: { interval_days: Number.isFinite(value) ? Math.min(365, Math.max(1, Math.round(value))) : 1 } };
        }
        if (path === '/categories') return { data: await categories() };
        if (path === '/activities') return { data: await activities(config.params || {}) };
        if (path === '/activities/suggestions') {
            const child = await activeChild();
            if (!child?.age_stage_id) return { data: [] };
            const pinned = await supabase
                .from('pinned_suggestions')
                .select('activity_id, activities(id, age_stage_id, category_id, titulo, objetivo, materiais, passos, duracao_min, cuidados, imagem_url, disclaimer, categories(id, slug, nome, cor, icone), age_stages(id, slug, titulo))')
                .eq('age_stage_id', child.age_stage_id)
                .order('created_at', { ascending: true })
                .limit(5);
            if (pinned.error) throw httpError(pinned.error.message);
            if (pinned.data?.length) return { data: pinned.data.map((item) => decorateActivity(item.activities)).filter(Boolean) };
            return { data: (await activities({ age_stage_id: child.age_stage_id })).slice(0, 5) };
        }
        if (path.startsWith('/activities/')) return { data: await activityById(path.split('/')[2]) };
        if (path === '/age-stages') return { data: await stages() };
        if (path.startsWith('/age-stages/')) {
            const { data, error } = await supabase.from('age_stages').select('*').eq('id', path.split('/')[2]).single();
            return ensureData(data, error);
        }
        if (path === '/notifications') {
            const profile = await currentProfile();
            const now = new Date().toISOString();
            const { data, error } = await supabase.from('notifications').select('id, user_id, kind, title, body, read_at, created_at, campaign_id, priority, action_url, expires_at').eq('user_id', profile.id).or(`expires_at.is.null,expires_at.gt.${now}`).order('created_at', { ascending: false }).limit(50);
            return ensureData(data || [], error);
        }
        if (path === '/notifications/unread-count') {
            const profile = await currentProfile();
            const { count, error } = await supabase.from('notifications').select('id', { count: 'exact', head: true }).eq('user_id', profile.id).is('read_at', null);
            if (error) throw httpError(error.message);
            return { data: { count: count || 0 } };
        }
        if (path === '/notification-preferences') {
            const profile = await currentProfile();
            const { data, error } = await supabase.from('notification_preferences').select('user_id, in_app_enabled, browser_enabled, email_enabled, updated_at').eq('user_id', profile.id).maybeSingle();
            if (error) throw httpError(error.message);
            return { data: data || { user_id: profile.id, in_app_enabled: true, browser_enabled: false, email_enabled: true } };
        }
        if (path === '/support/tickets') {
            const { data, error } = await supabase.from('support_tickets').select('id, user_id, subject, category, priority, status, assigned_to, last_message_at, created_at, updated_at').order('updated_at', { ascending: false }).limit(50);
            return ensureData(data || [], error);
        }
        if (path.startsWith('/support/tickets/')) {
            const id = path.split('/')[3];
            const ticket = await supabase.from('support_tickets').select('id, user_id, subject, category, priority, status, assigned_to, last_message_at, created_at, updated_at').eq('id', id).single();
            if (ticket.error) throw httpError(ticket.error.message);
            const messages = await supabase.from('support_messages').select('id, ticket_id, author_user_id, body, internal_note, created_at').eq('ticket_id', id).order('created_at', { ascending: true });
            if (messages.error) throw httpError(messages.error.message);
            return { data: { ...ticket.data, messages: messages.data || [] } };
        }
        if (path === '/favorites') {
            const child = await activeChild();
            if (!child) return { data: [] };
            const { data, error } = await supabase.from('favorites').select('id, child_id, activity_id, created_at').eq('child_id', child.id).order('created_at', { ascending: false });
            return ensureData(data || [], error);
        }
        if (path === '/progress/summary') {
            const child = await activeChild();
            if (!child) return { data: { week_count: 0, month_count: 0, by_category: [] } };
            const now = new Date();
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() - 6);
            const monthStart = new Date(now);
            monthStart.setDate(now.getDate() - 29);
            const { data: completions, error } = await supabase
                .from('completions')
                .select('id, activity_id, data, activities(category_id)')
                .eq('child_id', child.id)
                .gte('data', monthStart.toISOString())
                .order('data', { ascending: false });
            if (error) throw httpError(error.message);
            const list = completions || [];
            const week_count = list.filter((item) => new Date(item.data) >= weekStart).length;
            const categoryList = await categories();
            const totals = categoryList.map((category) => ({ category_id: category.id, slug: category.slug, nome: category.nome, cor: category.cor, count: 0 }));
            list.forEach((item) => {
                const category = totals.find((entry) => entry.category_id === item.activities?.category_id);
                if (category) category.count += 1;
            });
            return { data: { week_count, month_count: list.length, by_category: totals } };
        }
        if (path.startsWith('/invites/')) {
            const token = path.split('/')[2];
            const { data, error } = await supabase.from('invites').select('id, email, role, token, expires_at, invited_by_name, used').eq('token', token).eq('used', false).gt('expires_at', new Date().toISOString()).maybeSingle();
            if (error) throw httpError(error.message);
            if (!data) throw httpError('Convite inválido, expirado ou já utilizado.', 404);
            return { data: decorateInvite(data) };
        }
        if (path === '/admin/notification-campaigns') {
            await assertSuperAdmin();
            let query = supabase.from('notification_campaigns').select('id, title, body, kind, priority, action_url, expires_at, audience_type, audience_value, status, scheduled_for, recipient_count, created_by, published_by, published_at, created_at, updated_at').order('created_at', { ascending: false }).limit(100);
            if (config.params?.status) query = query.eq('status', config.params.status);
            const { data, error } = await query;
            await recordAudit('SELECT', 'notification_campaigns', null, { count: data?.length || 0, filters: config.params || {} });
            return ensureData(data || [], error);
        }
        if (path === '/admin/support-tickets') {
            await assertStaff();
            let query = supabase.from('support_tickets').select('id, user_id, subject, category, priority, status, assigned_to, last_message_at, created_at, updated_at, users!support_tickets_user_id_fkey(email, name)').order('updated_at', { ascending: false }).limit(100);
            if (config.params?.status) query = query.eq('status', config.params.status);
            if (config.params?.priority) query = query.eq('priority', config.params.priority);
            if (config.params?.q) query = query.ilike('subject', `%${String(config.params.q).replace(/[%,]/g, '')}%`);
            const { data, error } = await query;
            await recordAudit('SELECT', 'support_tickets', null, { count: data?.length || 0, filters: config.params || {} });
            return ensureData(data || [], error);
        }
        if (path.startsWith('/admin/support-tickets/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const ticket = await supabase.from('support_tickets').select('id, user_id, subject, category, priority, status, assigned_to, last_message_at, created_at, updated_at, users!support_tickets_user_id_fkey(email, name)').eq('id', id).single();
            if (ticket.error) throw httpError(ticket.error.message);
            const messages = await supabase.from('support_messages').select('id, ticket_id, author_user_id, body, internal_note, created_at').eq('ticket_id', id).order('created_at', { ascending: true });
            if (messages.error) throw httpError(messages.error.message);
            return { data: { ...ticket.data, messages: messages.data || [] } };
        }
        if (path === '/admin/stats') {
            await assertStaff();
            const [users, children, activities, favorites, banned] = await Promise.all([
                countRows('users'),
                countRows('children'),
                countRows('activities'),
                countRows('favorites'),
                supabase.from('users').select('id', { count: 'exact', head: true }).eq('banned', true),
            ]);
            if (banned.error) throw httpError(banned.error.message);

            const today = new Date();
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - 6);
            const monthStart = new Date(today);
            monthStart.setDate(today.getDate() - 29);
            const weekStartIso = weekStart.toISOString();
            const monthStartIso = monthStart.toISOString();

            const [{ count: weekSignups, error: signupsError }, { data: completionRows, error: completionsError }] = await Promise.all([
                supabase.from('users').select('id', { count: 'exact', head: true }).gte('created_at', weekStartIso),
                supabase.from('completions').select('id, activity_id, data, activities(category_id, titulo)').gte('data', monthStartIso).order('data', { ascending: false }),
            ]);
            if (signupsError) throw httpError(signupsError.message);
            if (completionsError) throw httpError(completionsError.message);

            const categoryList = await categories();
            const byCategory = categoryList.map((category) => ({ category_id: category.id, nome: category.nome, cor: category.cor, count: 0 }));
            const topMap = new Map();
            (completionRows || []).forEach((row) => {
                const category = byCategory.find((item) => item.category_id === row.activities?.category_id);
                if (category) category.count += 1;
                const activityId = row.activity_id;
                const current = topMap.get(activityId) || { activity_id: activityId, titulo: row.activities?.titulo || 'Atividade', count: 0 };
                current.count += 1;
                topMap.set(activityId, current);
            });

            const result = {
                data: {
                    totals: {
                        users,
                        children,
                        activities,
                        completions: completionRows?.length || 0,
                        favorites,
                        banned: banned.count || 0,
                    },
                    activity: {
                        week_signups: weekSignups || 0,
                        week_completions: (completionRows || []).filter((row) => new Date(row.data) >= weekStart).length,
                        month_completions: completionRows?.length || 0,
                    },
                    by_category: byCategory,
                    top_activities: Array.from(topMap.values()).sort((a, b) => b.count - a.count).slice(0, 5),
                },
            };
            await recordAudit('SELECT', 'admin_stats', null, { period_days: 30 });
            return result;
        }
        if (path === '/admin/users') {
            await assertStaff();
            const data = await adminUsers(config.params || {});
            await recordAudit('SELECT', 'users', null, { count: data.length, filters: config.params || {} });
            return { data };
        }
        if (path.startsWith('/admin/users/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { data: user, error } = await supabase.from('users').select('id, email, name, role, banned, accept_terms, created_at').eq('id', id).single();
            if (error) throw httpError(error.message);
            const { data: children, error: childError } = await supabase.from('children').select('id, nome, dob, foto_url').eq('user_id', id).order('created_at', { ascending: true });
            if (childError) throw httpError(childError.message);
            const childIds = (children || []).map((child) => child.id);
            let completions_count = 0;
            if (childIds.length) {
                const { count, error: completionError } = await supabase.from('completions').select('id', { count: 'exact', head: true }).in('child_id', childIds);
                if (completionError) throw httpError(completionError.message);
                completions_count = count || 0;
            }
            return { data: { ...user, children: children || [], completions_count } };
        }
        if (path === '/admin/activities') {
            const data = await activities(config.params || {});
            await recordAudit('SELECT', 'activities', null, { count: data.length, filters: config.params || {} });
            return { data };
        }
        if (path === '/admin/invites') {
            await assertSuperAdmin();
            const { data, error } = await supabase.from('invites').select('id, email, role, token, expires_at, invited_by_name, used, created_at').order('created_at', { ascending: false });
            await recordAudit('SELECT', 'invites', null, { count: data?.length || 0 });
            return ensureData((data || []).map(decorateInvite), error);
        }
        if (path === '/admin/settings') {
            await assertContentStaff();
            const { data, error } = await supabase.from('app_settings').select('key, value_json, updated_by, updated_at').order('key', { ascending: true });
            await recordAudit('SELECT', 'app_settings', null, { count: data?.length || 0 });
            return ensureData(data || [], error);
        }
        if (path === '/admin/email-providers') {
            await assertSuperAdmin();
            const { data, error } = await supabase.from('email_providers').select('id, provider, name, enabled, from_email, from_name, reply_to, secret_env_name, config_json, last_tested_at, last_test_status, last_test_message, updated_at').order('provider', { ascending: true });
            await recordAudit('SELECT', 'email_providers', null, { count: data?.length || 0 });
            return ensureData(data || [], error);
        }
        if (path === '/admin/email-deliveries') {
            await assertSuperAdmin();
            const params = config.params || {};
            const limit = Math.min(100, Math.max(1, Number(params.limit) || 40));
            let query = supabase.from('email_deliveries').select('id, provider_id, event_type, recipient, subject, status, provider_message_id, error_message, created_at').order('created_at', { ascending: false }).limit(limit);
            if (params.status) query = query.eq('status', params.status);
            if (params.event_type) query = query.eq('event_type', params.event_type);
            const { data, error } = await query;
            await recordAudit('SELECT', 'email_deliveries', null, { count: data?.length || 0 });
            return ensureData(data || [], error);
        }
        if (path === '/admin/audit-logs') {
            await assertSuperAdmin();
            const params = config.params || {};
            const limit = Math.min(200, Math.max(1, Number(params.limit) || 80));
            let query = supabase.from('audit_logs').select('id, action, resource, resource_id, actor_user_id, actor_email, actor_name, details, created_at').order('created_at', { ascending: false }).limit(limit);
            if (params.action) query = query.eq('action', params.action);
            if (params.resource) query = query.ilike('resource', `%${String(params.resource).replace(/[%,]/g, '')}%`);
            if (params.actor_email) query = query.ilike('actor_email', `%${String(params.actor_email).replace(/[%,]/g, '')}%`);
            if (params.from) query = query.gte('created_at', params.from);
            if (params.to) query = query.lte('created_at', params.to);
            const { data, error } = await query;
            await recordAudit('SELECT', 'audit_logs', null, { count: data?.length || 0, filters: params });
            return ensureData(data || [], error);
        }
        if (path === '/admin/prompts') {
            await assertContentStaff();
            const { data, error } = await supabase.from('admin_prompts').select('prompt_key, kind, name, system_prompt, user_prompt, output_schema, updated_by, updated_at').order('kind', { ascending: true }).order('name', { ascending: true });
            return ensureData(data || [], error);
        }
        if (path === '/admin/ai-models') {
            await assertContentStaff();
            return { data: await invokeAdminAI({ action: 'models' }) };
        }
        if (path === '/admin/ai-jobs') {
            await assertContentStaff();
            const limit = Math.min(100, Math.max(1, Number(config.params?.limit) || 40));
            const { data, error } = await supabase.from('ai_generation_jobs').select('id, kind, model, prompt_key, input_json, output_json, status, error_message, created_by, created_at, completed_at').order('created_at', { ascending: false }).limit(limit);
            return ensureData(data || [], error);
        }
        if (path === '/admin/pinned-suggestions') {
            await assertStaff();
            let query = supabase.from('pinned_suggestions').select('id, age_stage_id, activity_id, created_at, activities(id, titulo), age_stages(id, titulo)').order('created_at', { ascending: true });
            if (config.params?.age_stage_id) query = query.eq('age_stage_id', config.params.age_stage_id);
            const { data, error } = await query;
            return ensureData(data || [], error);
        }
        throw httpError(`Rota não implementada no Supabase: GET ${path}`, 404);
    },

    async post(path, payload, config = {}) {
        if (path === '/notifications/read-all') {
            const profile = await currentProfile();
            const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('user_id', profile.id).is('read_at', null);
            if (error) throw httpError(error.message);
            return { data: { ok: true } };
        }
        if (path.startsWith('/notifications/') && path.endsWith('/read')) {
            const profile = await currentProfile();
            const id = path.split('/')[2];
            const { data, error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id).eq('user_id', profile.id).select('id, read_at').single();
            return ensureData(data, error);
        }
        if (path === '/notification-preferences') {
            const profile = await currentProfile();
            const { data, error } = await supabase.from('notification_preferences').upsert({ user_id: profile.id, in_app_enabled: payload.in_app_enabled !== false, browser_enabled: Boolean(payload.browser_enabled), email_enabled: payload.email_enabled !== false, updated_at: new Date().toISOString() }, { onConflict: 'user_id' }).select('user_id, in_app_enabled, browser_enabled, email_enabled, updated_at').single();
            return ensureData(data, error);
        }
        if (path === '/support/tickets') {
            const { data, error } = await supabase.rpc('create_support_ticket', { p_subject: payload.subject, p_category: payload.category, p_priority: payload.priority, p_body: payload.body });
            if (error) throw httpError(error.message);
            return { data: { id: data } };
        }
        if (path.startsWith('/support/tickets/') && path.endsWith('/messages')) {
            const id = path.split('/')[3];
            const { data, error } = await supabase.rpc('reply_support_ticket', { p_ticket_id: id, p_body: payload.body, p_internal_note: false });
            if (error) throw httpError(error.message);
            return { data: { id: data } };
        }
        if (path.startsWith('/admin/notification-campaigns/') && path.endsWith('/publish')) {
            await assertSuperAdmin();
            const id = path.split('/')[3];
            const { data, error } = await supabase.rpc('publish_notification_campaign', { p_campaign_id: id });
            if (error) throw httpError(error.message);
            return { data: { recipient_count: data || 0 } };
        }
        if (path.startsWith('/admin/support-tickets/') && path.endsWith('/messages')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.rpc('reply_support_ticket', { p_ticket_id: id, p_body: payload.body, p_internal_note: Boolean(payload.internal_note) });
            if (error) throw httpError(error.message);
            return { data: { id: data } };
        }
        if (path === '/auth/login') {
            const { data, error } = await supabase.auth.signInWithPassword({ email: payload.email, password: payload.password });
            if (error) throw httpError(error.message, 401);
            const profile = await currentProfile();
            return { data: { ...profile, token: data.session?.access_token } };
        }
        if (path === '/auth/register') {
            const { data, error } = await supabase.auth.signUp({
                email: payload.email,
                password: payload.password,
                options: { data: { name: payload.name, accept_terms: Boolean(payload.accept_terms) } },
            });
            if (error) throw httpError(error.message);
            if (!data.session) return { data: { pending_confirmation: true, email: payload.email } };
            const profile = await currentProfile();
            return { data: { ...profile, token: data.session.access_token } };
        }
        if (path === '/auth/logout') {
            const { error } = await supabase.auth.signOut();
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path === '/children') {
            const profile = await currentProfile();
            const { data, error } = await supabase.from('children').insert({ user_id: profile.id, nome: payload.nome, dob: payload.dob, foto_url: payload.foto_url || null }).select('*').single();
            return ensureData(data, error);
        }
        if (path === '/children/active') {
            const profile = await currentProfile();
            const { data, error } = await supabase.from('users').update({ active_child_id: payload.child_id, updated_at: new Date().toISOString() }).eq('id', profile.id).select('active_child_id').single();
            return ensureData(data, error);
        }
        if (path === '/favorites/toggle') {
            const child = await activeChild();
            if (!child) throw httpError('Cadastre uma criança antes de favoritar atividades.');
            const existing = await supabase.from('favorites').select('id').eq('child_id', child.id).eq('activity_id', payload.activity_id).maybeSingle();
            if (existing.error) throw httpError(existing.error.message);
            if (existing.data) {
                const { error } = await supabase.from('favorites').delete().eq('id', existing.data.id);
                if (error) throw httpError(error.message);
                return { data: { favorited: false } };
            }
            const { error } = await supabase.from('favorites').insert({ child_id: child.id, activity_id: payload.activity_id });
            if (error) throw httpError(error.message);
            return { data: { favorited: true } };
        }
        if (path === '/completions') {
            const child = await activeChild();
            if (!child) throw httpError('Cadastre uma criança antes de concluir atividades.');
            const { data, error } = await supabase.from('completions').insert({ child_id: child.id, activity_id: payload.activity_id }).select('id, child_id, activity_id, data').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/invites/') && path.endsWith('/accept')) {
            const token = path.split('/')[2];
            const invite = await api.get(`/invites/${token}`);
            const { data, error } = await supabase.auth.signUp({ email: invite.data.email, password: payload.password, options: { data: { name: payload.name, accept_terms: true } } });
            if (error) throw httpError(error.message);
            if (!data.session) return { data: { pending_confirmation: true, email: invite.data.email, invite_token: token } };
            const claimed = await api.post(`/invites/${token}/claim`);
            return { data: { ...claimed.data, token: data.session.access_token } };
        }
        if (path.startsWith('/invites/') && path.endsWith('/claim')) {
            const token = path.split('/')[2];
            const { data, error } = await supabase.rpc('accept_invite', { p_token: token });
            if (error) throw httpError(error.message);
            const profile = await currentProfile();
            return { data: { ...profile, invite: Array.isArray(data) ? data[0] : data } };
        }
        if (path === '/admin/activities/import/preview') {
            await assertContentStaff();
            try {
                const items = await parseImport(payload, config);
                const current = await activities();
                const existing = new Set(current.map((item) => item.id));
                const normalized = items.map((item) => normalizeCatalogActivity(item));
                const to_create = normalized.filter((item) => !existing.has(item.id));
                return { data: { total: items.length, created_count: to_create.length, skipped_count: items.length - to_create.length, error_count: 0, to_create, errors: [] } };
            } catch (error) {
                throw httpError(`Não foi possível ler o arquivo: ${error.message}`);
            }
        }
        if (path === '/admin/activities/import/commit') {
            await assertContentStaff();
            const items = payload.activities || [];
            const rows = items.map((item) => normalizeCatalogActivity(item));
            const invalid = rows.find((item) => !item.age_stage_id || !item.category_id || !item.titulo);
            if (invalid) throw httpError('Cada atividade precisa de fase, categoria e título.');
            const { data, error } = await supabase.from('activities').upsert(rows, { onConflict: 'id' }).select('id');
            return ensureData({ created_count: data?.length || 0 }, error);
        }
        if (path === '/admin/notification-campaigns') {
            const profile = await assertSuperAdmin();
            const { data, error } = await supabase.from('notification_campaigns').insert({ title: String(payload.title || '').trim(), body: String(payload.body || '').trim(), kind: payload.kind || 'announcement', priority: payload.priority || 'normal', action_url: payload.action_url || null, expires_at: payload.expires_at || null, audience_type: payload.audience_type || 'all', audience_value: payload.audience_value || null, status: payload.scheduled_for ? 'scheduled' : 'draft', scheduled_for: payload.scheduled_for || null, created_by: profile.id }).select('id, title, body, kind, priority, action_url, expires_at, audience_type, audience_value, status, scheduled_for, recipient_count, created_at, updated_at').single();
            return ensureData(data, error);
        }
        if (path === '/admin/activities') {
            await assertContentStaff();
            const row = normalizeCatalogActivity(payload);
            if (!row.age_stage_id || !row.category_id || !row.titulo) throw httpError('Informe fase, categoria e título da atividade.');
            const { data, error } = await supabase.from('activities').insert(row).select('*').single();
            return ensureData(decorateActivity(data), error);
        }
        if (path === '/admin/age-stages') {
            await assertContentStaff();
            const row = { id: catalogId('stage', payload.id), slug: String(payload.slug || '').trim(), titulo: String(payload.titulo || '').trim(), descricao: String(payload.descricao || ''), min_days: Number(payload.min_days) || 0, max_days: Math.max(Number(payload.max_days) || 0, Number(payload.min_days) || 0), dados_gerais: String(payload.dados_gerais || ''), desenvolvimento: String(payload.desenvolvimento || ''), dicas: String(payload.dicas || ''), cuidados: String(payload.cuidados || '') };
            if (!row.slug || !row.titulo) throw httpError('Informe slug e título da fase.');
            const { data, error } = await supabase.from('age_stages').insert(row).select('*').single();
            return ensureData(data, error);
        }
        if (path === '/admin/categories') {
            await assertContentStaff();
            const row = { id: catalogId('category', payload.id), slug: String(payload.slug || '').trim(), nome: String(payload.nome || '').trim(), cor: String(payload.cor || '#E87A5D').trim(), icone: String(payload.icone || 'Sparkles').trim() };
            if (!row.slug || !row.nome) throw httpError('Informe slug e nome da categoria.');
            const { data, error } = await supabase.from('categories').insert(row).select('*').single();
            return ensureData(data, error);
        }
        if (path === '/admin/pinned-suggestions') {
            await assertStaff();
            const { data, error } = await supabase.from('pinned_suggestions').insert(payload).select('*').single();
            return ensureData(data, error);
        }
        if (path === '/admin/prompts') {
            const profile = await assertContentStaff();
            const { data, error } = await supabase.from('admin_prompts').insert({ prompt_key: payload.prompt_key, kind: payload.kind, name: payload.name, system_prompt: payload.system_prompt || '', user_prompt: payload.user_prompt || '', output_schema: payload.output_schema || {}, updated_by: profile.id }).select('prompt_key, kind, name, system_prompt, user_prompt, output_schema, updated_by, updated_at').single();
            return ensureData(data, error);
        }
        if (path === '/admin/ai-generate') {
            await assertContentStaff();
            return { data: await invokeAdminAI({ action: 'generate', ...payload }) };
        }
        if (path === '/admin/email-test') {
            await assertSuperAdmin();
            return { data: await invokeEmailService({ action: 'test', provider: payload.provider, recipient: payload.recipient }) };
        }
        if (path === '/admin/email-send') {
            await assertSuperAdmin();
            return { data: await invokeEmailService({ action: 'send', ...payload }) };
        }
        if (path.startsWith('/admin/ai-apply/')) {
            await assertContentStaff();
            return { data: await invokeAdminAI({ action: 'apply', job_id: path.split('/')[3] }) };
        }
        if (path === '/admin/invites') {
            const profile = await assertSuperAdmin();
            const token = crypto.randomUUID();
            const { data: settingRows, error: settingError } = await supabase.from('app_settings').select('key, value_json').in('key', ['invites.expiration_days', 'invites.delivery_mode']);
            if (settingError) throw httpError(settingError.message);
            const settings = Object.fromEntries((settingRows || []).map((row) => [row.key, row.value_json]));
            const expirationDays = Math.min(30, Math.max(1, Number(settings['invites.expiration_days']) || 7));
            const expires_at = new Date(Date.now() + expirationDays * DAY_MS).toISOString();
            const email = String(payload.email || '').trim().toLowerCase();
            if (!email) throw httpError('Informe o e-mail do convidado.');
            if (!['moderador', 'editor', 'super_admin'].includes(payload.role)) throw httpError('Selecione um papel válido.');
            const { data, error } = await supabase.from('invites').insert({ email, role: payload.role, token, expires_at, invited_by: profile.id, invited_by_name: profile.name || 'Admin' }).select('id, email, role, token, expires_at, invited_by_name, used, created_at').single();
            if (error) throw httpError(error.message);
            const invite = decorateInvite(data);
            const defaultProvider = String((await supabase.from('app_settings').select('value_json').eq('key', 'email.default_provider').maybeSingle()).data?.value_json || 'native');
            let email_result = { sent: false, mode: 'manual', provider: defaultProvider, reason: defaultProvider === 'native' ? 'O provedor nativo depende do SMTP/Auth do Supabase; compartilhe o link manualmente.' : 'O envio automático ainda não foi executado.' };
            if (defaultProvider !== 'native') {
                try {
                    const emailData = await invokeEmailService({ action: 'send', provider: defaultProvider, to: email, subject: 'Você recebeu um convite para o Crescer+', event_type: 'invite', html: `<div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;color:#3F302C"><h1 style="color:#C9684C">Convite para o Crescer+</h1><p>${escapeHtml(profile.name || 'A equipe Crescer+')} convidou você para atuar como <strong>${escapeHtml(payload.role)}</strong>.</p><p><a href="${escapeHtml(invite.link)}" style="display:inline-block;background:#C9684C;color:#fff;padding:14px 22px;border-radius:28px;text-decoration:none">Aceitar convite</a></p><p style="font-size:12px;color:#766862">Este link expira em ${expirationDays} dia(s). Conteúdo administrativo e educativo do Crescer+.</p></div>`, text: `${profile.name || 'A equipe Crescer+'} convidou você para atuar como ${payload.role}. Acesse: ${invite.link}. Este link expira em ${expirationDays} dia(s).` });
                    email_result = { sent: emailData.status === 'sent', mode: 'automatic', provider: emailData.provider, reason: emailData.fallback_used ? 'Enviado pelo provedor de fallback.' : 'E-mail enviado.' };
                } catch (emailError) {
                    email_result = { sent: false, mode: 'manual', provider: defaultProvider, reason: formatApiError(emailError.message || emailError) };
                }
            }
            return { data: { ...invite, email_result } };
        }
        if (path.startsWith('/admin/users/') && path.endsWith('/reset-password')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { data: user, error } = await supabase.from('users').select('email').eq('id', id).single();
            if (error) throw httpError(error.message);
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email, { redirectTo: window.location.origin + '/perfil' });
            if (resetError) throw httpError(resetError.message);
            return { data: { new_password: 'Link de redefinição enviado ao e-mail do usuário.' } };
        }
        if (path === '/admin/activities/import/commit') throw httpError('Importação inválida.');
        throw httpError(`Rota não implementada no Supabase: POST ${path}`, 404);
    },

    async put(path, payload) {
        if (path === '/notification-preferences') {
            const profile = await currentProfile();
            const { data, error } = await supabase.from('notification_preferences').upsert({ user_id: profile.id, in_app_enabled: payload.in_app_enabled !== false, browser_enabled: Boolean(payload.browser_enabled), email_enabled: payload.email_enabled !== false, updated_at: new Date().toISOString() }, { onConflict: 'user_id' }).select('user_id, in_app_enabled, browser_enabled, email_enabled, updated_at').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/admin/notification-campaigns/')) {
            const profile = await assertSuperAdmin();
            const id = path.split('/')[3];
            const allowed = { title: String(payload.title || '').trim(), body: String(payload.body || '').trim(), kind: payload.kind || 'announcement', priority: payload.priority || 'normal', action_url: payload.action_url || null, expires_at: payload.expires_at || null, audience_type: payload.audience_type || 'all', audience_value: payload.audience_value || null, status: payload.status || 'draft', scheduled_for: payload.scheduled_for || null, updated_at: new Date().toISOString() };
            const { data, error } = await supabase.from('notification_campaigns').update(allowed).eq('id', id).select('id, title, body, kind, priority, action_url, expires_at, audience_type, audience_value, status, scheduled_for, recipient_count, created_at, updated_at').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/admin/support-tickets/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.from('support_tickets').update({ status: payload.status, priority: payload.priority, category: payload.category, assigned_to: payload.assigned_to || null, updated_at: new Date().toISOString() }).eq('id', id).select('id, status, priority, category, assigned_to, updated_at').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/admin/email-providers/')) {
            const profile = await assertSuperAdmin();
            const provider = decodeURIComponent(path.split('/')[3]);
            if (!['native', 'resend', 'mailtrap'].includes(provider)) throw httpError('Provedor de e-mail inválido.');
            const update = { from_email: String(payload.from_email || '').trim(), from_name: String(payload.from_name || 'Crescer+').trim(), reply_to: String(payload.reply_to || '').trim(), updated_by: profile.id, updated_at: new Date().toISOString() };
            const { data, error } = await supabase.from('email_providers').update(update).eq('provider', provider).select('id, provider, name, enabled, from_email, from_name, reply_to, secret_env_name, config_json, last_tested_at, last_test_status, last_test_message, updated_at').single();
            if (error) throw httpError(error.message);
            return { data };
        }
        if (path.startsWith('/admin/settings/')) {
            const key = decodeURIComponent(path.split('/')[3]);
            const profile = key.startsWith('invites.') || key.startsWith('email.') ? await assertSuperAdmin() : await assertContentStaff();
            const value_json = Object.prototype.hasOwnProperty.call(payload || {}, 'value_json') ? payload.value_json : payload?.value;
            const { data, error } = await supabase.from('app_settings').upsert({ key, value_json: value_json ?? null, updated_by: profile.id, updated_at: new Date().toISOString() }, { onConflict: 'key' }).select('key, value_json, updated_by, updated_at').single();
            return ensureData(data, error);
        }
        if (path === '/admin/email-provider-select') {
            const profile = await assertSuperAdmin();
            const provider = String(payload.provider || '');
            if (!['native', 'resend', 'mailtrap'].includes(provider)) throw httpError('Provedor de e-mail inválido.');
            const target = await supabase.from('email_providers').select('id, provider').eq('provider', provider).single();
            if (target.error) throw httpError(target.error.message);
            const { error: disableError } = await supabase.from('email_providers').update({ enabled: false, updated_by: profile.id, updated_at: new Date().toISOString() }).neq('provider', provider);
            if (disableError) throw httpError(disableError.message);
            const { data, error } = await supabase.from('email_providers').update({ enabled: true, updated_by: profile.id, updated_at: new Date().toISOString() }).eq('provider', provider).select('id, provider, enabled').single();
            if (error) throw httpError(error.message);
            await supabase.from('app_settings').upsert({ key: 'email.default_provider', value_json: provider, updated_by: profile.id, updated_at: new Date().toISOString() }, { onConflict: 'key' });
            return { data };
        }
        if (path.startsWith('/admin/prompts/')) {
            const profile = await assertContentStaff();
            const prompt_key = decodeURIComponent(path.split('/')[3]);
            const { data, error } = await supabase.from('admin_prompts').update({ kind: payload.kind, name: payload.name, system_prompt: payload.system_prompt, user_prompt: payload.user_prompt, output_schema: payload.output_schema, updated_by: profile.id, updated_at: new Date().toISOString() }).eq('prompt_key', prompt_key).select('prompt_key, kind, name, system_prompt, user_prompt, output_schema, updated_by, updated_at').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/children/')) {
            const profile = await currentProfile();
            const id = path.split('/')[2];
            const { data, error } = await supabase.from('children').update({ nome: payload.nome, dob: payload.dob, foto_url: payload.foto_url || null, updated_at: new Date().toISOString() }).eq('id', id).eq('user_id', profile.id).select('*').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/admin/activities/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.from('activities').update({ ...payload, disclaimer: payload.disclaimer || DEFAULT_DISCLAIMER, updated_at: new Date().toISOString() }).eq('id', id).select('*').single();
            return ensureData(decorateActivity(data), error);
        }
        if (path.startsWith('/admin/age-stages/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.from('age_stages').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id).select('*').single();
            return ensureData(data, error);
        }
        if (path.startsWith('/admin/categories/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.from('categories').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id).select('*').single();
            return ensureData(data, error);
        }
        throw httpError(`Rota não implementada no Supabase: PUT ${path}`, 404);
    },

    async patch(path, payload) {
        if (path.startsWith('/admin/users/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { data, error } = await supabase.from('users').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id).select('id, email, name, role, banned, accept_terms, created_at').single();
            return ensureData(data, error);
        }
        throw httpError(`Rota não implementada no Supabase: PATCH ${path}`, 404);
    },

    async delete(path) {
        if (path.startsWith('/admin/notification-campaigns/')) {
            await assertSuperAdmin();
            const id = path.split('/')[3];
            const { error } = await supabase.from('notification_campaigns').delete().eq('id', id).in('status', ['draft', 'archived']);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path === '/auth/account') {
            const { error } = await supabase.rpc('delete_my_account');
            if (error) throw httpError(error.message);
            await supabase.auth.signOut();
            return { data: null };
        }
        if (path.startsWith('/children/')) {
            const profile = await currentProfile();
            const id = path.split('/')[2];
            const { error } = await supabase.from('children').delete().eq('id', id).eq('user_id', profile.id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/prompts/')) {
            await assertContentStaff();
            const prompt_key = decodeURIComponent(path.split('/')[3]);
            const { error } = await supabase.from('admin_prompts').delete().eq('prompt_key', prompt_key);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/activities/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { error } = await supabase.from('activities').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/age-stages/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { error } = await supabase.from('age_stages').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/categories/')) {
            await assertContentStaff();
            const id = path.split('/')[3];
            const { error } = await supabase.from('categories').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/pinned-suggestions/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { error } = await supabase.from('pinned_suggestions').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/invites/')) {
            await assertSuperAdmin();
            const id = path.split('/')[3];
            const { error } = await supabase.from('invites').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        if (path.startsWith('/admin/users/')) {
            await assertStaff();
            const id = path.split('/')[3];
            const { error } = await supabase.from('users').delete().eq('id', id);
            if (error) throw httpError(error.message);
            return { data: null };
        }
        throw httpError(`Rota não implementada no Supabase: DELETE ${path}`, 404);
    },
};

export function formatApiError(detail) {
    if (detail == null) return 'Algo deu errado. Tente novamente.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((item) => (item && typeof item.msg === 'string' ? item.msg : JSON.stringify(item))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}

export default api;
